import { useAuth } from '@teko/auth-react';
import { Policy } from '@teko/ecommerce/mobile';
import { useFeatureFlags } from 'contexts';
import DOMPurify from 'dompurify';
import useSWR from 'swr';
import { useTenantConfig } from '../hooks';
import { publicService } from '../services';
import { ProductPolicy } from '../types';

const OTHER_POLICIES = 0;

const convertPolicies = (policies: ProductPolicy[]): Policy[] => {
  return policies.map((el, index) => ({
    id: index,
    content: el.displayText,
    icon: el.iconUrl,
    detailUrl: el.detailUrl,
  }));
};

export const usePolicies = (sku: string, sellerId?: number) => {
  const {
    terminalId,
    currentLanguage,
    terminal,
    useTerminalId,
    isHighlightedOnPromotionBlock,
  } = useTenantConfig();
  const { flags } = useFeatureFlags();
  const { loading, user } = useAuth();

  const { data: allPolicies = [] } = useSWR(
    !!sku && !loading ? ['/sale-policies', sku, sellerId, user?.id] : null,
    async () => {
      const { policies } = await publicService.getSalePoliciesBySku(
        {
          sku,
          ...(flags.epic_omni_1237 ? { sellerId } : {}),
          ...(flags.epic_ecom_284 || useTerminalId
            ? { terminalId }
            : { terminalCode: terminal }),
          isHighlightedOnPromotionBlock,
        },
        currentLanguage,
      );
      return policies.map((policy) => ({
        ...policy,
        // after remove 305 --> displayText is required param
        displayText: DOMPurify.sanitize(policy.displayText || ''),
      }));
    },
    { revalidateOnFocus: false },
  );

  const salePolicies = allPolicies.filter(
    (policy) =>
      policy.serviceGroupId !== OTHER_POLICIES &&
      (!!policy.isHighlightedOnProductDetail || !isHighlightedOnPromotionBlock),
  );
  const otherPolicies = allPolicies.filter(
    (policy) =>
      policy.serviceGroupId === OTHER_POLICIES &&
      (!!policy.isHighlightedOnProductDetail || !isHighlightedOnPromotionBlock),
  );
  const paymentPromotionPolicies = allPolicies.filter(
    (policy) => !!policy.isHighlightedOnPromotionBlock,
  );

  return {
    policies: convertPolicies(allPolicies),
    salePolicies: convertPolicies(salePolicies),
    otherPolicies: convertPolicies(otherPolicies),
    paymentPromotionPolicies: convertPolicies(paymentPromotionPolicies),
  };
};
